// AI Step 6: Residual Risk & Recommendations
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Scale, Gauge, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { RiskSliders, DualDimensionResult } from './RiskHelpers';

export default function Step6ResidualRisk({ formData, updateField, calculateRisk, calculating, riskConfig }) {
  const initialScore = formData.initial_risk_score;
  const residualScore = formData.residual_risk_score;
  const delta = initialScore !== null && residualScore !== null ? Math.round(initialScore - residualScore) : null;

  const RingChart = ({ score, className: cls }) => (
    <div className={cn("relative w-20 h-20 mx-auto", cls)}>
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
        {score !== null && (
          <circle cx="50" cy="50" r="45" fill="none" strokeWidth="8" strokeLinecap="round"
            stroke={score <= 25 ? '#059669' : score <= 50 ? '#D97706' : score <= 75 ? '#DC2626' : '#991B1B'}
            strokeDasharray={`${score * 2.83} 283`} />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold">{score !== null ? Math.round(score) : '?'}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Residual Risk & Recommendations</h2>
        <p className="text-muted-foreground mt-1">Re-assess risk after controls are documented.</p>
      </div>

      {initialScore !== null && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4 text-blue-500" />Risk Comparison</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center"><RingChart score={initialScore} /><p className="text-sm text-muted-foreground mt-2">Initial Risk</p></div>
              <div className="flex flex-col items-center">
                <ArrowRight className="h-8 w-8 text-muted-foreground" />
                {delta !== null && <Badge className={cn("mt-1", delta > 0 ? "bg-green-500" : delta < 0 ? "bg-red-500" : "bg-gray-500")}>{delta > 0 ? `-${delta}` : delta < 0 ? `+${Math.abs(delta)}` : '0'} pts</Badge>}
              </div>
              <div className="text-center"><RingChart score={residualScore} /><p className="text-sm text-muted-foreground mt-2">Residual Risk</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Gauge className="h-4 w-4 text-green-500" />Residual Risk Variables</CardTitle>
          <CardDescription>Adjust sliders to reflect risk after controls</CardDescription>
        </CardHeader>
        <CardContent>
          <RiskSliders riskType="residual" formData={formData} updateField={updateField} riskConfig={riskConfig} />
          <div className="mt-6 pt-4 border-t">
            <Button onClick={() => calculateRisk('residual')} disabled={calculating} data-testid="calculate-residual-risk">
              <Gauge className="h-4 w-4 mr-2" />{calculating ? 'Calculating...' : 'Calculate Residual Risk'}
            </Button>
          </div>
          <DualDimensionResult riskType="residual" formData={formData} />
        </CardContent>
      </Card>

      {residualScore !== null && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Risk Acceptability</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Button type="button" variant={formData.risk_acceptable === true ? "default" : "outline"} onClick={() => updateField('risk_acceptable', true)} className="flex-1">
                <CheckCircle2 className="h-4 w-4 mr-2" />Risk Acceptable
              </Button>
              <Button type="button" variant={formData.risk_acceptable === false ? "destructive" : "outline"} onClick={() => updateField('risk_acceptable', false)} className="flex-1">
                <AlertTriangle className="h-4 w-4 mr-2" />Risk Not Acceptable
              </Button>
            </div>
            <div>
              <Label>Justification</Label>
              <Textarea value={formData.risk_acceptability_justification || ''} onChange={e => updateField('risk_acceptability_justification', e.target.value)} placeholder="Explain why the residual risk is or is not acceptable..." className="mt-1.5" rows={3} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
